import { logError } from '../../shared/utils/logger';
import { BASE_URL, fetchWithAuth } from './client';

const delay = (ms = 400) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * RQ: { userId: string, caseId?: string }
 * RS: {
 *   status: 200,
 *   data: Array<{
 *     id,
 *     caseNumber,
 *     name,
 *     email,
 *     loanType,
 *     status,
 *     date,
 *     propertyAddress,
 *     loanAmount,
 *     documents: Array<{ name, size, source, uploadedAt, previewUrl? }>,
 *     aiReports: Array<{ id, name, status, eta, url? }>,
 *     comments: Array<{ id, text, author, createdAt }>
 *   }>
 * }
 */
export async function fetchCases(payload = { userId: "" }) {
    await delay(300);
    return {
        status: 200,
        data: []
    };
}

/**
 * Collab cases list for a user
 * RQ: { userId: string }
 * RS: { status: 200, data: { cases: Array<{ caseId, caseNumber, borrowerName, status, sharedWithCount, role }> } }
 */
export async function fetchCollabCases(payload = { userId: "" }) {
    const userId = encodeURIComponent(payload.userId || "");
    const response = await fetchWithAuth(`${BASE_URL}/api/cases?scope=collab&userId=${userId}`, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Collab cases fetch failed:", response.status, errorText);
        throw new Error("Unable to load shared cases. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data };
}

/**
 * Dashboard summary for a user.
 * RQ: { userId: string }
 * RS: {
 *   status: 200,
 *   data: {
 *     stats: {
 *       underwriting: number,
 *       processing: number,
 *       actionRequired: number,
 *       approved: number,
 *       clearedToClose: number
 *     },
 *     cases: Array<...same shape as fetchCases response items...>
 *   }
 * }
 */
export async function fetchDashboard(payload = { userId: "" }) {
    const userId = encodeURIComponent(payload.userId || "");
    // List endpoint no longer includes documents - fetch via GET /api/cases/{caseId} instead
    const response = await fetchWithAuth(`${BASE_URL}/api/cases?userId=${userId}`, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Dashboard fetch failed:", response.status, errorText);
        throw new Error("Unable to load dashboard. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data };
}

/**
 * RQ: { name: string, propertyAddress: string, loanType: string, loanAmount: string }
 * RS: { status: 200, data: { id, caseNumber, name, loanType, status, date, propertyAddress, loanAmount } }
 */
export async function createCase(payload) {
    try {
        const response = await fetchWithAuth(`${BASE_URL}/api/cases/create`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                ...payload,
                userId: payload.userId || payload.lenderEmail || payload.email || ""
            })
        });
        if (!response.ok) {
            throw new Error("Unable to create case. Please try again.");
        }
        const data = await response.json();
        return { status: response.status, data };
    } catch (error) {
        logError("Create case error:", error);
        throw error;
    }
}

/**
 * Update case details
 * PATCH /api/cases/{caseId}
 * RQ: { caseId: string, name?: string, propertyAddress?: string, loanType?: string, loanAmount?: string|number, email?: string, status?: string }
 * RS: { status: 200, data: { id, caseNumber, name, email, loanType, status, propertyAddress, loanAmount, ... } }
 */
export async function updateCase(payload) {
    if (!payload?.caseId) {
        throw new Error("Case ID is required to update case.");
    }

    const { caseId, ...updateFields } = payload;

    const response = await fetchWithAuth(`${BASE_URL}/api/cases/${encodeURIComponent(caseId)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateFields)
    });

    if (response.status === 403) {
        throw new Error("You don't have permission to update this case.");
    }

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Update case failed:", response.status, errorText);
        throw new Error("Unable to update case. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data };
}

/**
 * RQ: { caseId: string, newStatus: string }
 * RS: { status: 200, data: { caseId, status: string } }
 */
export async function updateCaseStatus(payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/cases/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            caseId: payload.caseId,
            newStatus: payload.newStatus
        })
    });

    if (!response.ok) {
        throw new Error("Unable to update status. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data };
}

/**
 * RQ: { caseId: string, files: File[] | Array<{ name, size, type, content? }> }
 * RS: { status: 200, data: Array<{ name, size, source, uploadedAt, previewUrl? }> }
 */
export async function uploadCaseDocuments(payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/cases/documents`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            caseId: payload.caseId,
            files: payload.files
        })
    });

    if (!response.ok) {
        throw new Error("Unable to upload documents. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data };
}

/**
 * RQ: { caseId: string, text: string, author: string }
 * RS: { status: 200, data: { id, text, author, createdAt } }
 */
export async function addCaseComment(payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/cases/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            caseId: payload.caseId,
            text: payload.text,
            author: payload.author
        })
    });

    if (!response.ok) {
        throw new Error("Unable to add comment. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data };
}

/**
 * Documents: get fresh presigned URL by objectKey.
 */
export async function fetchSignedUrl(objectKey) {
    const key = encodeURIComponent(objectKey || "");
    const response = await fetchWithAuth(`${BASE_URL}/api/cases/documents/signed-url?objectKey=${key}`);
    if (!response.ok) {
        throw new Error("Unable to load document preview.");
    }
    const data = await response.json();
    return { status: response.status, data };
}

/**
 * Cases: fetch by case number (returns case without documents)
 * To get documents, use fetchCaseById with the returned case ID
 */
export async function fetchCaseByNumber(caseNumber) {
    const numberParam = encodeURIComponent(caseNumber || "");
    // List/lookup endpoints no longer include documents
    const url = `${BASE_URL}/api/cases/by-number?caseNumber=${numberParam}`;
    const response = await fetchWithAuth(url, {
        headers: { accept: "application/json" }
    });
    if (!response.ok) {
        throw new Error("Unable to load case details. Please try again.");
    }
    const data = await response.json();
    return { status: response.status, data };
}

/**
 * Fetch case by ID with documents and fresh signed URLs
 * RQ: GET /api/cases/{caseId}
 * RS: {
 *   status: 200,
 *   data: {
 *     id: string,
 *     userId: string,
 *     caseNumber: string,
 *     name: string,
 *     email: string,
 *     loanType: string,
 *     status: string,
 *     date: string,
 *     propertyAddress: string,
 *     loanAmount: string,
 *     documents: Array<{
 *       name: string,
 *       size: number,
 *       source: string,
 *       uploadedAt: string,
 *       objectKey: string,
 *       contentType: string,
 *       signedUrl: string,
 *       downloadUrl: string,
 *       previewUrl: string
 *     }>
 *   }
 * }
 */
export async function fetchCaseById(caseId) {
    if (!caseId) {
        throw new Error("Unable to load case details. Please try again.");
    }

    const response = await fetchWithAuth(`${BASE_URL}/api/cases/${encodeURIComponent(caseId)}`, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch case by ID failed:", response.status, errorText);
        throw new Error("Unable to load case details. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data };
}

/**
 * Collaboration summary for a case
 * RQ: GET /api/cases/{caseId}/collaboration
 * RS: {
 *   status: 200,
 *   data: {
 *     caseId: string,
 *     plan: { tier: string, inviteLimitPerCase: number },
 *     usage: { invitesSent: number, remainingInvites: number },
 *     collaborators: Array<{ email: string, status: string, invitedAt?: string }>
 *   }
 * }
 */
export async function fetchCaseCollaboration(caseId) {
    if (!caseId) {
        throw new Error("Unable to load collaboration details. Please try again.");
    }
    const response = await fetchWithAuth(`${BASE_URL}/api/cases/${encodeURIComponent(caseId)}/collaboration`, {
        headers: { accept: "application/json" }
    });
    // Gracefully handle 404 - endpoint may not exist yet
    if (response.status === 404) {
        return { status: 200, data: { plan: null, usage: null, collaborators: [] } };
    }
    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch collaboration summary failed:", response.status, errorText);
        throw new Error("Unable to load collaboration details. Please try again.");
    }
    // Handle empty response body
    try {
        const data = await response.json();
        return { status: response.status, data };
    } catch {
        return { status: response.status, data: { plan: null, usage: null, collaborators: [] } };
    }
}

/**
 * Create a collaborator invite for a case
 * RQ: { caseId: string, email: string, role?: string }
 * RS: { status: 201, data: { inviteId, email, status, usage } }
 */
export async function createCaseCollaboratorInvite(payload) {
    if (!payload?.caseId || !payload?.email) {
        throw new Error("Unable to send invite. Please try again.");
    }
    const response = await fetchWithAuth(
        `${BASE_URL}/api/cases/${encodeURIComponent(payload.caseId)}/collaborators/invites`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: payload.email, role: payload.role || "collaborator" })
        }
    );
    let data = null;
    try {
        data = await response.json();
    } catch (error) {
        data = null;
    }
    if (!response.ok) {
        const err = new Error(data?.message || "Unable to send invite. Please try again.");
        err.status = response.status;
        err.data = data;
        throw err;
    }
    return { status: response.status, data };
}

/**
 * Revoke/remove a collaborator for a case
 * RQ: { caseId: string, collaboratorId: string }
 * RS: { status: 200, data: { status: string, usage?: object } }
 */
export async function revokeCaseCollaborator(payload) {
    if (!payload?.caseId || !payload?.collaboratorId) {
        throw new Error("Unable to remove collaborator. Please try again.");
    }
    const response = await fetchWithAuth(
        `${BASE_URL}/api/cases/${encodeURIComponent(payload.caseId)}/collaborators/${encodeURIComponent(payload.collaboratorId)}`,
        { method: "DELETE" }
    );
    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Remove collaborator failed:", response.status, errorText);
        throw new Error("Unable to remove collaborator. Please try again.");
    }
    const data = await response.json();
    return { status: response.status, data };
}

/**
 * Delete a document from a case
 * RQ: { objectKey: string }
 * RS: { status: 200, data: { message: string, objectKey: string } }
 */
export async function deleteDocument(objectKey) {
    if (!objectKey) {
        throw new Error("Missing objectKey");
    }
    const response = await fetchWithAuth(
        `${BASE_URL}/api/cases/documents?objectKey=${encodeURIComponent(objectKey)}`,
        { method: "DELETE" }
    );
    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Delete document failed:", response.status, errorText);
        throw new Error("Unable to delete document. Please try again.");
    }
    const data = await response.json();
    return { status: response.status, data };
}

/**
 * Request AI report generation for a case
 * RQ: { 
 *   caseId: string, 
 *   batchSize?: number, 
 *   bucket?: string, 
 *   force?: boolean,
 *   runId?: string,
 *   intakeDate?: string
 * }
 * RS: { status: 200, data: { ok: boolean, failedEntryCount: number } }
 */
export async function requestAiReport(payload) {
    if (!payload?.caseId) {
        throw new Error("Case ID is required to generate AI report.");
    }

    // DEBUG LOGGING AS REQUESTED
    console.log("Preparing AI Report Request:", {
        caseId: payload.caseId,
        membership: payload.membership || "free",
        report_type: payload.reportType || "basic",
        delivery: payload.delivery || "standard",
        total_cost: payload.totalCost
    });

    const requestBody = {
        caseId: payload.caseId,
        membership: payload.membership,
        report_type: payload.reportType,
        delivery: payload.delivery,
        total_cost: payload.totalCost,
        // Legacy fields for backward compatibility if needed
        batchSize: payload.batchSize || 100,
        bucket: payload.bucket || "cloudrealtor-io-bucket",
        force: payload.force || false,
    };

    // Optional fields
    if (payload.runId) requestBody.runId = payload.runId;
    if (payload.intakeDate) requestBody.intakeDate = payload.intakeDate;

    // Use NEW endpoint
    const postRequest = async (url) => fetchWithAuth(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", accept: "application/json" },
        body: JSON.stringify(requestBody)
    });

    let response = await postRequest(`${BASE_URL}/api/cases/ai-report`);

    if (!response.ok) {
        let errorData = null;
        try {
            errorData = await response.json();
        } catch (e) { /* Ignore JSON parse error */ }

        const errorText = errorData ? JSON.stringify(errorData) : await response.text().catch(() => "Unknown error");
        logError("AI report request failed:", response.status, errorText);

        const error = new Error(errorData?.message || errorData?.detail?.message || "Unable to generate AI report. Please try again.");
        error.status = response.status;
        error.data = errorData;
        throw error;
    }

    const data = await response.json();
    return { status: response.status, data };
}

/**
 * Fetch pipeline run status from a tracking URL.
 * trackingUrl can be absolute or relative (e.g. /api/v1/cases/{caseId}/runs/{runId})
 */
export async function fetchRunStatus(trackingUrl) {
    if (!trackingUrl) {
        throw new Error("Tracking URL is required.");
    }
    const normalizedTrackingUrl = trackingUrl.startsWith("/v1/")
        ? `/api${trackingUrl}`
        : trackingUrl.startsWith("v1/")
            ? `/api/${trackingUrl}`
            : trackingUrl;
    const url = normalizedTrackingUrl.startsWith("http")
        ? normalizedTrackingUrl
        : `${BASE_URL}${normalizedTrackingUrl}`;
    const response = await fetchWithAuth(url, {
        headers: { accept: "application/json" }
    });
    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch run status failed:", response.status, errorText);
        throw new Error("Unable to fetch run status.");
    }
    const data = await response.json();
    return { status: response.status, data };
}

/**
 * Fetch pipeline runs for a case
 * RQ: { caseId: string, limit?: number }
 * RS: { status: 200, data: { executions: Array<{ run_id, status, started_at }> } }
 */
export async function fetchCaseRuns(payload) {
    if (!payload?.caseId) {
        throw new Error("Case ID is required to fetch runs.");
    }
    const limit = payload.limit || 5;
    const response = await fetchWithAuth(`${BASE_URL}/api/v1/cases/${encodeURIComponent(payload.caseId)}/runs?limit=${limit}`, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch runs failed:", response.status, errorText);
        throw new Error("Unable to fetch case runs.");
    }

    const data = await response.json();
    return { status: response.status, data };
}

export async function fetchRunReportUrl(caseId, runId) {
    if (!caseId || !runId) throw new Error("Missing caseId or runId");

    const response = await fetchWithAuth(`${BASE_URL}/api/v1/cases/${encodeURIComponent(caseId)}/runs/${encodeURIComponent(runId)}/report-url`, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        throw new Error("Failed to fetch report URL");
    }

    const data = await response.json();
    return data;
}

export async function fetchRunReport(caseId, runId) {
    if (!caseId || !runId) throw new Error("Missing caseId or runId");

    const response = await fetchWithAuth(
        `${BASE_URL}/api/v1/cases/${encodeURIComponent(caseId)}/runs/${encodeURIComponent(runId)}/report`,
        { headers: { accept: "application/json" } }
    );

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch report failed:", response.status, errorText);
        throw new Error("Failed to fetch report.");
    }

    const data = await response.json();
    return { status: response.status, data };
}
